import { cloneDeep, isEqual } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { ElementType, ValueFormat } from '@/typings/designStudio/sourceElementTypes';
import { applySvgMaskClipPath, parseSvgMaskShape } from '@/utils/fabricUtils';
import { markImagePatternMember } from '@/utils/imagePatternMasks';
import type { CanvasEx } from '@/typings/canvas';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type {
    OriginTransform,
    PatternCell,
    PatternElement,
    PatternLink,
    PatternProps,
    PatternReplicaService,
    ReplicaPosition
} from '@/typings/customilyElements/patternElements';

/** fabric.ImagePlaceholder runtime members the replicas rely on that ImagePlaceholderElement doesn't declare */
type ImagePatternElement = ImagePlaceholderElement &
    PatternProps & {
        _emptyImage?: boolean;
        originalMaskElement?: HTMLImageElement | null;
        originalMaskSvg?: string | null;
        hasMask?: boolean;
        maskPath?: string | null;
        maskChanged?: boolean;
        imageUrl?: string;
        imageMode?: string;
        outlineWidth?: number;
        outlineColor?: string;
        enablePerspective?: boolean;
        perspectiveCoords?: number[][] | null;
        __updatePerspectiveCoords: (coords: number[][]) => void;
        setMask: (mask: HTMLImageElement | null) => void;
        setMode: (mode: string) => void;
        setScale: (width: number, height: number, updateReferences?: boolean) => void;
        setOpacity: (opacity: number) => void;
        getOpacity: () => number;
        loadShadow: (properties: Record<string, unknown>) => void;
    };

type ImagePlaceholderClass = new (
    element: HTMLImageElement | null,
    options: Record<string, unknown>
) => ImagePatternElement;

type SvgMaskTarget = Parameters<typeof applySvgMaskClipPath>[0];

/** State copied as is. Image, mask, size, color mode, outline and shadow go through their own setters below */
const SYNCED_PROPS = [
    'color',
    'colors',
    'colorLibraryId',
    'selectedColorPosition',
    'imageColor',
    'threshold',
    'fullColor',
    'grading',
    'rotationStep',
    'removeWhiteBackground',
    'RemoveBackground',
    'CutoutFace',
    'removeBackgroundCOP',
    'CustomilyRemoveBackground',
    'CustomilyFaceCutOut',
    'CustomilyImageFilter',
    'textToImage',
    'imageToImage',
    'imageToImageConfig',
    'imageEffects',
    'coverMaskArea',
    'isLimitImageToPlaceholder',
    'lockMovement',
    'lockScaling',
    'limitMovement',
    'allowEdit',
    'preventEditMode',
    'allowSwapLibraryPosition',
    'initCenterX',
    'initCenterY',
    'initWidth',
    'initHeight',
    'initRotation',
    'hasStaticImage',
    'imageUrl',
    'testingUrl',
    'isOverlay',
    'isColorOptions',
    'podProductImagesMetadata',
    'skewX',
    'skewY',
    'name'
] as const;

/** Saved by productService but left out of ImagePlaceholder.toObject() */
const PROPS_MISSING_FROM_TO_OBJECT = [
    'imageUrl',
    'testingUrl',
    'isOverlay',
    'isColorOptions',
    'podProductImagesMetadata'
] as const;

/** Positioning of the image inside the placeholder: changing it needs a re-layout, not new filters */
const INIT_POSITION_PROPS = new Set<string>(['initCenterX', 'initCenterY', 'initWidth', 'initHeight', 'initRotation']);

const asImage = (element: PatternElement) => (element as unknown) as ImagePatternElement;

/** Changes flow origin -> replicas only: a shared array or object would let an unlocked replica edit its origin */
const ownCopy = <T>(value: T): T => (value !== null && typeof value === 'object' ? cloneDeep(value) : value);

const getImagePlaceholderClass = (): ImagePlaceholderClass =>
    ((window as unknown) as { fabric: { ImagePlaceholder: ImagePlaceholderClass } }).fabric.ImagePlaceholder;

let svgMaskCache: { svg: string; shape: ReturnType<typeof parseSvgMaskShape> } | null = null;
const getSvgMaskShape = (svg: string) => {
    // Every replica uses the origin's mask: parse it once, applySvgMaskClipPath clones it per replica
    if (svgMaskCache?.svg !== svg) {
        const shape = parseSvgMaskShape(svg);
        // The parse can time out under load: a cached rejection would fail every later build with this mask
        shape.catch(() => {
            if (svgMaskCache?.shape === shape) svgMaskCache = null;
        });
        svgMaskCache = { svg, shape };
    }
    return svgMaskCache.shape;
};

/** Shares the origin's already decoded bitmaps instead of downloading them again like ImagePlaceholder.fromObject */
const copyImageAndMask = async (origin: ImagePatternElement, replica: ImagePatternElement) => {
    markImagePatternMember(origin);
    markImagePatternMember(replica);
    const svgMask = origin.originalMaskSvg || null;
    const innerImage = replica.item(0) as {
        clipPath?: { absolutePositioned?: boolean };
        set: (k: string, v: unknown) => void;
    };
    if (innerImage.clipPath?.absolutePositioned) innerImage.set('clipPath', null);

    replica.originalMaskSvg = null;
    // Same order as fromObject: setElement re-runs the mask sizing against the image
    replica.setMask(svgMask ? null : origin.originalMaskElement || null);
    replica.setElement((origin._emptyImage ? null : origin.originalElement) as HTMLImageElement);
    if (svgMask) {
        replica.originalMaskSvg = svgMask;
        await applySvgMaskClipPath((replica as unknown) as SvgMaskTarget, await getSvgMaskShape(svgMask));
    }

    copyMaskPath(origin, replica);
};

/** The origin uploads a new mask on save; replicas reuse its maskPath */
const copyMaskPath = (origin: ImagePatternElement, replica: ImagePatternElement) => {
    replica.maskPath = origin.maskPath;
    replica.maskChanged = false;
};

type ImageSource = { src?: string } | null | undefined;

/** Same bitmap: the same element, or another one loaded from the same URL (on load every placeholder loads its own) */
const sameSource = (a: ImageSource, b: ImageSource) => a === b || (!!a?.src && a.src === b?.src);

/** Whether the replica still shows another bitmap or mask than the origin, the only case setElement is needed */
const imageOrMaskDiffers = (origin: ImagePatternElement, replica: ImagePatternElement) => {
    const originImage = origin._emptyImage ? null : origin.originalElement;
    const replicaImage = replica._emptyImage ? null : replica.originalElement;
    const originSvg = origin.originalMaskSvg || null;
    if (!sameSource(originImage, replicaImage) || originSvg !== (replica.originalMaskSvg || null)) return true;
    return !originSvg && !sameSource(origin.originalMaskElement, replica.originalMaskElement);
};

/** fabric 4.6 fabric.Image internals behind a filter pass, not in the typings */
interface FilteredImage {
    filters?: Array<Record<string, unknown> | null>;
    _element?: HTMLImageElement | HTMLCanvasElement;
    _originalElement?: HTMLImageElement | HTMLCanvasElement;
    _filteredEl?: HTMLCanvasElement | null;
    _filterScalingX?: number;
    _filterScalingY?: number;
    _lastScaleX?: number;
    _lastScaleY?: number;
    enablePerspective?: boolean;
    _removePerspective: () => void;
    applyFilters: () => unknown;
    set: (key: string, value: unknown) => void;
}

const innerImage = (placeholder: ImagePatternElement) => (placeholder.item(0) as unknown) as FilteredImage;

/** Primitives and arrays of them, like Perspective's coords: filters can hold caches too, so not any object */
const isPlainSetting = (value: unknown): boolean =>
    value === null || typeof value !== 'object' || (Array.isArray(value) && value.every(isPlainSetting));

/** A filter's type and settings: its plain public own props (filters can hold caches, so not the whole object) */
const filtersKey = (image: FilteredImage) =>
    JSON.stringify(
        (image.filters ?? []).filter(Boolean).map((filter) => [
            // On the prototype, like every default: filters left at their defaults only differ by it
            filter!.type,
            Object.entries(filter!).filter(([key, value]) => !key.startsWith('_') && isPlainSetting(value))
        ])
    );

/** fabric.Image.prototype.applyFilters, past any instance override */
const runOwnFilters = (image: FilteredImage) =>
    (Object.getPrototypeOf(image) as FilteredImage).applyFilters.call(image);

const imageSrc = (image: FilteredImage) => (image._originalElement as ImageSource)?.src || null;

// Replica image -> a canvas it shows without owning it: its origin's filtered bitmap, or the blank one while waiting
const borrowed = new WeakMap<FilteredImage, HTMLCanvasElement>();

let blankCanvas: HTMLCanvasElement | null = null;
// Transparent: drawn at any size it shows nothing, instead of the unfiltered image
const blank = () =>
    blankCanvas ?? (blankCanvas = Object.assign(document.createElement('canvas'), { width: 1, height: 1 }));

const borrow = (image: FilteredImage, canvas: HTMLCanvasElement, scalingX = 1, scalingY = 1) => {
    image._element = canvas;
    image._filteredEl = canvas;
    image._filterScalingX = scalingX;
    image._filterScalingY = scalingY;
    image._lastScaleX = 1;
    image._lastScaleY = 1;
    borrowed.set(image, canvas);
};

/** fabric reuses _filteredEl in place: back on its own element first, so the pass gets a canvas of its own */
const unborrow = (image: FilteredImage) => {
    const canvas = borrowed.get(image);
    if (!canvas) return;
    borrowed.delete(image);
    if (image._element === canvas || image._filteredEl === canvas) {
        image._element = image._originalElement;
        image._filteredEl = null;
    }
};

/** Same as fabric's pass without filters */
const showUnfiltered = (image: FilteredImage) => {
    unborrow(image);
    image._element = image._originalElement;
    image._filteredEl = null;
    image._filterScalingX = 1;
    image._filterScalingY = 1;
};

/**
 * Same source and same filters give exactly the origin's filtered bitmap: shown as is instead of filtering again (tens
 *  of ms) or copying it (a canvas the size of the image per replica). The origin's later passes redraw it in place.
 */
const copyFromOrigin = (from: FilteredImage, to: FilteredImage): boolean => {
    if (!from._element) return false;
    if (from._element === from._originalElement) {
        showUnfiltered(to);
        return true;
    }
    borrow(to, from._element as HTMLCanvasElement, from._filterScalingX, from._filterScalingY);
    return true;
};

/** How long a replica waits for its origin's pass before running its own (e.g. the origin's image failed to load) */
const ORIGIN_PASS_TIMEOUT_MS = 3000;

interface ReplicaFilters {
    // Null while a load hasn't created the origin yet: resolved by originUuid from the load registry
    origin: ImagePatternElement | null;
    originUuid: string;
    registry: LoadRegistry | null;
    replica: ImagePatternElement;
    // > 0 while syncs/builds run the setters: their passes are only noted and settled once at the end
    deferred: number;
    requested: boolean;
    waitTimer: ReturnType<typeof setTimeout> | null;
}

const replicaFilters = new WeakMap<FilteredImage, ReplicaFilters>();
// Origin image -> replicas waiting for its next pass
const waitingForOrigin = new WeakMap<FilteredImage, Set<ReplicaFilters>>();
const originsWrapped = new WeakSet<FilteredImage>();

type PassOutcome = 'copied' | 'own' | 'waiting' | 'empty';

/** Placeholders of a canvas being loaded, by uuid: a replica can come before its origin */
interface LoadRegistry {
    members: Map<string, ImagePatternElement>;
    // Origin uuid -> replicas waiting for that origin to be created
    waiting: Map<string, Set<ReplicaFilters>>;
    // Every replica this load tracked, to stop their waits when the next load starts
    replicas: Set<ReplicaFilters>;
}

const loadRegistries = new WeakMap<object, LoadRegistry>();

const registryOf = (canvas: object) => {
    if (!loadRegistries.has(canvas)) {
        loadRegistries.set(canvas, { members: new Map(), waiting: new Map(), replicas: new Set() });
    }
    return loadRegistries.get(canvas)!;
};

interface WaitLists<K> {
    get(key: K): Set<ReplicaFilters> | undefined;
    set(key: K, value: Set<ReplicaFilters>): unknown;
}

const addWaiting = <K>(lists: WaitLists<K>, key: K, state: ReplicaFilters) => {
    if (!lists.get(key)) lists.set(key, new Set());
    lists.get(key)!.add(state);
};

const resolveOrigin = (state: ReplicaFilters) => {
    if (!state.origin) state.origin = state.registry?.members.get(state.originUuid) ?? null;
    return state.origin;
};

const stopWaiting = (state: ReplicaFilters) => {
    if (state.waitTimer) clearTimeout(state.waitTimer);
    state.waitTimer = null;
    if (state.origin) waitingForOrigin.get(innerImage(state.origin))?.delete(state);
    state.registry?.waiting.get(state.originUuid)?.delete(state);
};

/**
 * The replica's filter pass: the origin's bitmap when it has the same source and filters; a blank wait while the origin
 *  is still loading that image (instead of showing it unfiltered); its own pass otherwise, e.g. an unlocked replica edited
 *  on its own in step 2.
 */
const settleReplicaPass = (state: ReplicaFilters): PassOutcome => {
    stopWaiting(state);
    const to = innerImage(state.replica);
    const origin = resolveOrigin(state);
    const src = imageSrc(to);
    const finish = (outcome: PassOutcome) => {
        to.set('dirty', true);
        state.replica.set('dirty', true);
        return outcome;
    };
    const ownPass = () => {
        unborrow(to);
        runOwnFilters(to);
        return finish('own');
    };

    // Filtering the empty 1x1 image (before the real one arrives) draws nothing anyway: shown as fabric does without filters
    if (state.replica._emptyImage) {
        showUnfiltered(to);
        return finish('empty');
    }
    // A canvas bitmap (no src) can't be told apart from the origin's. Unlocked: detached, the origin redraws in place
    if (!src || state.replica.locked === false) return ownPass();
    if (origin) {
        const from = innerImage(origin);
        if (imageSrc(from) === src) {
            return filtersKey(from) === filtersKey(to) && copyFromOrigin(from, to) ? finish('copied') : ownPass();
        }
        // The origin is loading the same image: its persisted test image, or the default one while it's still empty
        const originLoadingIt = !imageSrc(from) || src === origin.testingUrl;
        if (!originLoadingIt) return ownPass();
    }

    // Waits for the origin's pass, or for the origin itself when the load hasn't created it yet
    borrow(to, blank());
    if (origin) addWaiting(waitingForOrigin, innerImage(origin), state);
    else if (state.registry) addWaiting(state.registry.waiting, state.originUuid, state);
    state.waitTimer = setTimeout(() => {
        stopWaiting(state);
        ownPass();
        ((state.replica as unknown) as { canvas?: CanvasEx }).canvas?.requestRenderAll();
    }, ORIGIN_PASS_TIMEOUT_MS);
    return finish('waiting');
};

/** After each of its own passes, the origin hands its bitmap to the replicas waiting for it */
const wrapOriginFilters = (origin: ImagePatternElement) => {
    const image = innerImage(origin);
    if (originsWrapped.has(image)) return;
    originsWrapped.add(image);
    image.applyFilters = function (this: FilteredImage) {
        const result = runOwnFilters(this);
        for (const state of Array.from(waitingForOrigin.get(this) ?? [])) settleReplicaPass(state);
        return result;
    };
};

const hasFabricImage = (placeholder: ImagePatternElement) =>
    typeof placeholder.item === 'function' && !!innerImage(placeholder);

/**
 * Routes every filter pass of an image replica through settleReplicaPass, for as long as it lives (built, synced or
 *  loaded). Only pixels change: the setters still leave the same filters and props, so what gets saved doesn't.
 */
const trackReplica = (
    replica: ImagePatternElement,
    origin: ImagePatternElement | null,
    originUuid: string,
    registry: LoadRegistry | null
) => {
    const image = innerImage(replica);
    if (origin) wrapOriginFilters(origin);
    const known = replicaFilters.get(image);
    if (known) {
        if (origin) known.origin = origin;
        return;
    }
    const state: ReplicaFilters = {
        origin,
        originUuid,
        registry,
        replica,
        deferred: 0,
        requested: false,
        waitTimer: null
    };
    replicaFilters.set(image, state);
    registry?.replicas.add(state);
    image.applyFilters = function (this: FilteredImage) {
        if (state.deferred) state.requested = true;
        else settleReplicaPass(state);
        return this;
    };
};

/** Also what populatePatternLinks hands every linked replica: only image ones copy their origin's filtered bitmap */
export const trackImageReplicaFilters = (originElement: PatternElement, replicaElement: PatternElement) => {
    if (originElement.type !== 'image-placeholder' || replicaElement.type !== 'image-placeholder') return;
    const origin = asImage(originElement);
    const replica = asImage(replicaElement);
    if (!hasFabricImage(origin) || !hasFabricImage(replica)) return;
    trackReplica(replica, origin, origin.uuid as string, null);
};

/** A replica just unlocked stops showing its origin's bitmap, which the origin's next passes would redraw in place */
export const releaseOriginBitmap = (replicaElement: PatternElement) => {
    if (replicaElement.type !== 'image-placeholder' || replicaElement.locked !== false) return;
    const replica = asImage(replicaElement);
    if (!hasFabricImage(replica)) return;
    const image = innerImage(replica);
    const state = replicaFilters.get(image);
    // Only while borrowing: object:modified also fires on every later edit of the unlocked replica
    if (state && borrowed.has(image)) settleReplicaPass(state);
};

/**
 * Called by the loaders right after creating a placeholder, before requesting its bitmap: loads that wait for a mask
 *  get their bitmaps (and filter passes) before populatePatternLinks runs. Links tell origins and replicas apart.
 */
export const trackImagePatternOnLoad = (
    canvas: object,
    element: PatternElement,
    links: PatternLink[] | null | undefined
) => {
    const placeholder = asImage(element);
    if (!placeholder.uuid || !hasFabricImage(placeholder)) return;
    const imageLinks = (links ?? []).filter(
        (link) => link.valueFormat === ValueFormat.Pattern && link.targetType === ElementType.Image
    );
    const asReplica = imageLinks.find((link) => link.targetId === placeholder.uuid);
    const isOrigin = imageLinks.some((link) => link.sourceId === placeholder.uuid);
    if (!asReplica && !isOrigin) return;

    // Before its mask is requested: the loader and the render share the mask results by this mark
    markImagePatternMember(placeholder);
    const registry = registryOf(canvas);
    registry.members.set(placeholder.uuid, placeholder);
    if (asReplica) {
        trackReplica(placeholder, registry.members.get(asReplica.sourceId) ?? null, asReplica.sourceId, registry);
    }
    if (isOrigin) {
        wrapOriginFilters(placeholder);
        // Replicas created before it now wait for its first pass
        const from = innerImage(placeholder);
        for (const state of Array.from(registry.waiting.get(placeholder.uuid) ?? [])) {
            registry.waiting.get(placeholder.uuid)!.delete(state);
            state.origin = placeholder;
            addWaiting(waitingForOrigin, from, state);
        }
    }
};

/**
 * Called by the loaders before loading a canvas again (e.g. another preview, with the same uuids): the previous load's
 *  placeholders are let go and can't be paired with the new ones, and its replicas stop waiting for their origins.
 */
export const resetImagePatternLoad = (canvas: object) => {
    const registry = loadRegistries.get(canvas);
    if (!registry) return;
    for (const state of Array.from(registry.replicas)) stopWaiting(state);
    loadRegistries.delete(canvas);
};

/** Runs the setters of a build/sync with the replica's passes held back, then settles them once */
const withDeferredFilters = async (
    origin: ImagePatternElement,
    replica: ImagePatternElement,
    work: () => Promise<void> | void
) => {
    trackImageReplicaFilters(origin, replica);
    const state = replicaFilters.get(innerImage(replica))!;
    state.deferred++;
    try {
        await work();
    } finally {
        state.deferred--;
    }
    if (state.deferred || !state.requested) return;
    state.requested = false;
    settleReplicaPass(state);
};

/** Mirrors the origin's on-canvas size: scale while it's being dragged, baked into width/height once it is */
const matchSize = (origin: ImagePatternElement, replica: ImagePatternElement, transform?: OriginTransform) => {
    const scaleX = transform?.scaleX ?? origin.scaleX!;
    const scaleY = transform?.scaleY ?? origin.scaleY!;
    replica.set({
        scaleX: (origin.width! * scaleX) / replica.width!,
        scaleY: (origin.height! * scaleY) / replica.height!
    });
    const originBaked = scaleX === 1 && scaleY === 1;
    if (originBaked && (replica.scaleX !== 1 || replica.scaleY !== 1)) {
        replica.setScale(replica.width!, replica.height!, true);
    }
};

/**
 * Moves the replica to its cell. `light` is for drag frames: the angle (which re-places the edit mode mask bounds)
 *  only when it changed and only the absolute coords fabric culls with; the gesture always ends with a full pass.
 */
const place = (
    origin: ImagePatternElement,
    replica: ImagePatternElement,
    position?: ReplicaPosition,
    light = false
) => {
    const angle = position?.transform?.angle ?? origin.angle;
    if (!light || replica.angle !== angle) replica.set('angle', angle);
    if (position) replica.set({ left: position.left, top: position.top });
    if (light) {
        // fabric 4.6 internals, not in the typings: absolute coords only, skipping the control corners
        const coords = (replica as unknown) as { aCoords: unknown; calcCoords: (absolute: boolean) => unknown };
        coords.aCoords = coords.calcCoords(true);
        ((replica as unknown) as { _updateSvgClipPath: () => void })._updateSvgClipPath();
    } else {
        // Also moves an SVG mask clip path along with the placeholder
        replica.setCoords();
    }
};

/** The perspective in effect: turning it on without moving a corner yet leaves no coords */
const activePerspective = (placeholder: ImagePatternElement) =>
    placeholder.enablePerspective && placeholder.perspectiveCoords?.length ? placeholder.perspectiveCoords : null;

/** Previews only (step 2). Coords are percentages of the image, so the origin's apply to its replicas as they are */
const syncPerspective = (origin: ImagePatternElement, replica: ImagePatternElement) => {
    const coords = activePerspective(origin);
    if (isEqual(coords, activePerspective(replica))) return;
    if (coords) {
        replica.enablePerspective = true;
        innerImage(replica).enablePerspective = true;
        replica.__updatePerspectiveCoords(ownCopy(coords));
        return;
    }
    // Not the placeholder's _removePerspective: it also leaves its edit mode and selects the replica
    innerImage(replica)._removePerspective();
    replica.enablePerspective = false;
    replica.perspectiveCoords = null;
};

/** Returns the plain props that changed. Every filter related setter below re-applies its filters by itself */
const syncState = (origin: ImagePatternElement, replica: ImagePatternElement): string[] => {
    const source = (origin as unknown) as Record<string, unknown>;
    const target = (replica as unknown) as Record<string, unknown>;
    const changed: string[] = [];
    for (const key of SYNCED_PROPS) {
        if (isEqual(target[key], source[key])) continue;
        replica.set(key as never, ownCopy(source[key]) as never);
        changed.push(key);
    }

    const { flipX, flipY } = origin.getFlip();
    replica.setFlip(flipX, flipY);
    replica.setOpacity(origin.getOpacity());

    if (replica.imageMode !== origin.imageMode && origin.imageMode) replica.setMode(origin.imageMode);
    if (replica.outlineColor !== origin.outlineColor)
        replica.set('imageOutlineColor' as never, origin.outlineColor as never);
    if (replica.outlineWidth !== origin.outlineWidth)
        replica.set('imageOutlineWidth' as never, origin.outlineWidth as never);

    replica.loadShadow({
        shadowType: origin.shadowType,
        shadowOffset: origin.shadowOffset,
        shadowAngle: origin.shadowAngle,
        shadowBlur: origin.shadowBlur,
        shadowColor: ownCopy(origin.shadowColor),
        shadowColorsMap: ownCopy(origin.shadowColorsMap),
        shadowColorOption: origin.shadowColorOption,
        shadowColorLibraryId: origin.shadowColorLibraryId
    });
    replica.setShadowColor(origin.getShadowColor());
    syncPerspective(origin, replica);
    return changed;
};

const buildPatternReplica: PatternReplicaService['buildPatternReplica'] = async (
    canvas: CanvasEx,
    originElement: PatternElement,
    cell: PatternCell,
    options
) => {
    const origin = asImage(originElement);
    const props: Record<string, unknown> = origin.toObject();
    delete props.uuid;
    delete props.imageSrc;
    delete props.maskImageSrc;
    // Serialized as a plain object, the constructor rebuilds the bounds clip path
    props.clipPath = undefined;
    for (const key of PROPS_MISSING_FROM_TO_OBJECT) {
        props[key] = ((origin as unknown) as Record<string, unknown>)[key];
    }
    for (const key of [...SYNCED_PROPS, 'perspectiveCoords']) {
        if (key in props) props[key] = ownCopy(props[key]);
    }

    const ImagePlaceholder = getImagePlaceholderClass();
    const replica = new ImagePlaceholder(null, props);
    await withDeferredFilters(origin, replica, async () => {
        await copyImageAndMask(origin, replica);
        // toObject skips the flip (it's on item(0)) and the constructor the outline filter; syncState's diff misses both
        const { flipX, flipY } = origin.getFlip();
        replica.setFlip(flipX, flipY);
        if (origin.outlineWidth) replica.set('imageOutlineWidth' as never, origin.outlineWidth as never);
    });

    replica.uuid = options.uuid || uuidv4();
    replica.id = origin.id;
    replica.isPatternReplica = true;
    replica.patternOriginUuid = origin.uuid;
    replica.pattern = false;
    replica.patternConfig = null;
    replica.patternCell = { row: cell.row, col: cell.col };
    // isPatternReplica is set first so locking doesn't paint the red "locked" border
    replica.set('locked', true);
    if (typeof options.zIndex === 'number') replica.set('zIndex', options.zIndex);
    replica.addedOnEdit = !!options.editMode;
    // toObject copied the origin's scale relative to the selection holding it
    if (options.transform) matchSize(origin, replica, options.transform);
    place(origin, replica, { left: options.left, top: options.top, transform: options.transform });

    // Replicas belong to the origin, a separate undo entry per replica would orphan their links
    canvas.addWithoutHistory((replica as unknown) as PatternElement);
    return (replica as unknown) as PatternElement;
};

const repositionPatternReplica: PatternReplicaService['repositionPatternReplica'] = (
    originElement,
    replicaElement,
    position,
    resize
) => {
    const origin = asImage(originElement);
    const replica = asImage(replicaElement);
    if (resize) matchSize(origin, replica, position.transform);
    // Only resize passes end a gesture (object:modified); move/rotate frames take the light path
    place(origin, replica, position, !resize);
};

const syncPatternReplica: PatternReplicaService['syncPatternReplica'] = async (
    _canvas,
    originElement,
    replicaElement,
    position
) => {
    const origin = asImage(originElement);
    const replica = asImage(replicaElement);
    matchSize(origin, replica, position?.transform);
    await withDeferredFilters(origin, replica, async () => {
        const changed = syncState(origin, replica);
        if (imageOrMaskDiffers(origin, replica)) {
            await copyImageAndMask(origin, replica);
        } else {
            if (changed.some((key) => INIT_POSITION_PROPS.has(key))) {
                // setMask re-lays the image out from the init values without running the filters again
                replica.setMask(replica.originalMaskSvg ? null : replica.originalMaskElement || null);
            }
            copyMaskPath(origin, replica);
        }
    });
    place(origin, replica, position);
};

export const imagePatternReplicaService: PatternReplicaService = {
    elementType: ElementType.Image,
    // Filters make an image replica cost up to tens of ms: batches keep the editor responsive
    batchSize: 8,
    // Since replicas share the origin's bitmaps most syncs cost ~1 ms per replica: yielding after every batch took
    //  ~90% of a resync (round 32). Two frames of work per yield keeps it responsive
    batchTimeBudgetMs: 32,
    buildPatternReplica,
    repositionPatternReplica,
    syncPatternReplica
};

/** Can hold an uploaded file as a data URL: kept out of the stringified state and compared by reference */
const URL_PROPS = new Set<string>(['imageUrl', 'testingUrl']);
const STATE_PROPS = SYNCED_PROPS.filter((key) => !URL_PROPS.has(key));

/**
 * Step 2 writes a test image change only to the step 1 entry of the selected placeholder. The rest of its pattern
 *  must follow there too, or step 3 reloads them with the previous image (copies made by hand keep that behavior).
 */
export const copyTestingUrlToPatternEntries = (
    entries: Array<{ uuid?: string; testingUrl?: string | null }>,
    sourceUuid: string,
    patternUuids: string[]
): number => {
    const source = entries.find((entry) => entry.uuid === sourceUuid);
    if (!source) return 0;
    const members = new Set(patternUuids);
    let copied = 0;
    for (const entry of entries) {
        if (entry === source || !entry.uuid || !members.has(entry.uuid)) continue;
        entry.testingUrl = source.testingUrl;
        copied++;
    }
    return copied;
};

/** Replica uuid -> origin uuid, image patterns only: text pattern replicas keep their own test values */
const getImageReplicaOrigins = (links: PatternLink[] | null | undefined) =>
    new Map(
        (links ?? [])
            .filter((link) => link.valueFormat === ValueFormat.Pattern && link.targetType === ElementType.Image)
            .map((link) => [link.targetId, link.sourceId])
    );

/**
 * IndexedDB keeps a test value per element and a replica's is always its origin's data URL, stored once per replica.
 *  Only the origin's is saved; withImageReplicaTestValues gives it back to the replicas on load.
 */
export const dropImageReplicaTestValues = <T extends { uuid?: string }>(
    values: T[],
    links: PatternLink[] | null | undefined
): T[] => {
    const replicaOrigins = getImageReplicaOrigins(links);
    return values.filter((value) => !value.uuid || !replicaOrigins.has(value.uuid));
};

/** Replicas get their origin's stored test value, the only one of the pattern dropImageReplicaTestValues keeps */
export const withImageReplicaTestValues = <T extends { elementId: string }>(
    values: T[] | null | undefined,
    links: PatternLink[] | null | undefined
): T[] | null | undefined => {
    const replicaOrigins = getImageReplicaOrigins(links);
    if (!values?.length || !replicaOrigins.size) return values;
    const byId = new Map(values.map((value) => [value.elementId, value]));
    // Rows a replica got saved before only origins kept one: stale once the origin's changed or was removed
    const result = values.filter((value) => !replicaOrigins.has(value.elementId));
    for (const [replicaId, originId] of replicaOrigins) {
        const origin = byId.get(originId);
        if (origin) result.push({ ...origin, elementId: replicaId });
    }
    return result;
};

interface OriginSnapshot {
    state: string;
    imageUrl: unknown;
    testingUrl: unknown;
    // Elements, not their src: a data URL can weigh megabytes and only gets compared when the reference changes
    image: unknown;
    mask: unknown;
    svg: unknown;
}

/** Everything syncPatternReplica replays. Transforms are left out, repositionPatternReplica follows those */
const takeSnapshot = (origin: ImagePatternElement): OriginSnapshot => {
    const source = (origin as unknown) as Record<string, unknown>;
    const { flipX, flipY } = origin.getFlip();
    return {
        imageUrl: source.imageUrl,
        testingUrl: source.testingUrl,
        state: JSON.stringify([
            STATE_PROPS.map((key) => source[key]),
            origin.imageMode,
            origin.outlineColor,
            origin.outlineWidth,
            origin.shadowType,
            origin.shadowOffset,
            origin.shadowAngle,
            origin.shadowBlur,
            origin.getShadowColor(),
            origin.getOpacity(),
            flipX,
            flipY,
            origin.maskPath,
            // The saved option, apart from the css above: trying colors out paints the same css the pick then stores
            origin.shadowColor,
            Array.from(origin.shadowColorsMap ?? []),
            origin.shadowColorOption,
            origin.shadowColorLibraryId,
            activePerspective(origin)
        ]),
        image: origin._emptyImage ? null : origin.originalElement,
        mask: origin.originalMaskElement ?? null,
        svg: origin.originalMaskSvg ?? null
    };
};

const sameState = (a: OriginSnapshot, b: OriginSnapshot) =>
    a.state === b.state && a.imageUrl === b.imageUrl && a.testingUrl === b.testingUrl;

const sameSnapshot = (a: OriginSnapshot, b: OriginSnapshot) =>
    sameState(a, b) &&
    sameSource(a.image as ImageSource, b.image as ImageSource) &&
    sameSource(a.mask as ImageSource, b.mask as ImageSource) &&
    a.svg === b.svg;

/**
 * The origin's persisted test/static image, or the default one when it has none, finishing to load after an empty
 *  baseline: every replica loads its own copy too. Anything else that gives an empty origin an image (e.g. OpenAI on
 *  the selected one) still syncs.
 */
const isOwnImageLoading = (
    origin: ImagePatternElement,
    previous: OriginSnapshot,
    snapshot: OriginSnapshot,
    defaultImageSrc: string | null
) => {
    if (previous.image || !snapshot.image || !sameState(previous, snapshot)) return false;
    if (!sameSource(previous.mask as ImageSource, snapshot.mask as ImageSource) || previous.svg !== snapshot.svg) {
        return false;
    }
    const src = (snapshot.image as ImageSource)?.src;
    if (!src) return false;
    if (src === origin.testingUrl || src === origin.imageUrl) return true;
    return !origin.testingUrl && src === defaultImageSrc;
};

// img.src is always absolute, the bundled asset URL may not be
const resolveUrl = (url?: string) => (url ? new URL(url, document.baseURI).href : null);

/** Quiet time after the last change before replicas follow; coalesces bursts like dragging a color picker */
const AUTO_SYNC_DEBOUNCE_MS = 150;

/**
 * Image origins change from many places (angular handlers, Vue panels, test images), most of them outside
 *  any digest. Comparing a snapshot after each render catches all of them; the first one is the baseline.
 * The resync runs outside the render, debounced, and one at a time: changes made meanwhile are queued.
 */
export const createImagePatternAutoSync = (
    resync: (origin: PatternElement) => Promise<void>,
    options: { defaultImageUrl?: string } = {}
) => {
    const defaultImageSrc = resolveUrl(options.defaultImageUrl);
    // By object, not uuid: every preview has its own origin with the same uuid, and loading one isn't a change
    const snapshots = new WeakMap<object, OriginSnapshot>();
    const pending = new Map<string, PatternElement>();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let syncing = false;

    const schedule = () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(run, AUTO_SYNC_DEBOUNCE_MS);
    };

    const run = async () => {
        timer = null;
        if (syncing) return;
        syncing = true;
        try {
            for (const [uuid, origin] of Array.from(pending)) {
                pending.delete(uuid);
                await resync(origin);
            }
        } finally {
            syncing = false;
            if (pending.size) schedule();
        }
    };

    return (canvas: CanvasEx) => {
        for (const object of (canvas.getObjects() as unknown) as PatternElement[]) {
            if (object.type !== 'image-placeholder' || !object.pattern || !object.uuid) continue;
            const snapshot = takeSnapshot(asImage(object));
            const previous = snapshots.get(object);
            snapshots.set(object, snapshot);
            if (previous && !sameSnapshot(previous, snapshot)) {
                if (isOwnImageLoading(asImage(object), previous, snapshot, defaultImageSrc)) continue;
                pending.set(object.uuid, object);
                schedule();
            }
        }
    };
};
